import { Component } from '@poa/core';
import { observable, selector } from '@poa/state';
import cx from 'classnames';
import orderBy from 'lodash/orderBy';
import moment from 'moment';
import React from 'react';
import Pagination from 'react-js-pagination';
import DataTableBody from './data-table-body';
import { DataTableCaption } from './data-table-caption';
import { DataTableCounter } from './data-table-counter';
import { DataTableFilters } from './data-table-filters';
import { DataTableFooter } from './data-table-footer';
import { DataTableHeader } from './data-table-header';
import { getValueFromRowByHeader, isObservableArraysOrStringsEqual } from './data-table-helpers';
import Spinner from 'react-spinkit';

const parsedDatesCache = {};

class DataTable extends React.Component {
  static defaultProps = {
    ref: () => {},
    isDecimalThunk: () => true,
    tableFiltersKey: Date.now(),
    datetimesKeys: [],
    toNumberKeys: []
  };

  filters = observable([]);
  isOpened = observable(true);
  isRestoring = observable(true);
  loading = observable();
  headersInViewPortRight = observable(0);
  headersInViewPortLeft = observable(0);
  sortingMap = observable.map(this.props.initialSortingMap || {});

  headerEls = new Set();

  tableContainerEl;
  tableContainerRectRight;
  tableContainerRectLeft;
  tableEl;

  datesHeaders = ['transfer_date', 'register_date', 'date', 'fine_time'];

  data = selector(() => {
    const getters = [];
    const modes = [];

    const futureDate = new Date(3999, 1, 1); // used to show rows without date at first

    this.sortingMap.forEach((mode, key) => {
      if (mode === 'none') {
        return;
      }

      getters.push(row => {
        const value = getValueFromRowByHeader(row, key);

        if (value && this.props.toNumberKeys.includes(key)) {
          return parseInt(value, 10);
        }

        if (this.datesHeaders.includes(key)) {
          if (value) {
            if (!parsedDatesCache[value]) {
              if (this.props.datetimesKeys.includes(key)) {
                parsedDatesCache[value] = moment(value, 'DD.MM.YYYY HH:mm:ss');
                return parsedDatesCache[value];
              }

              if (typeof value === 'number') {
                // unix timestamp received
                parsedDatesCache[value] = moment(value);
              } else {
                parsedDatesCache[value] = moment(value, 'DD.MM.YYYY');
              }
            }

            return parsedDatesCache[value];
          } else {
            return futureDate;
          }
        }

        return value;
      });

      modes.push(mode);
    });

    const filteredBody = this.props.data.tbody.filter(row => {
      let shouldShow = true;

      this.headersOrder.get().forEach(columnSetting => {
        const rowBySection = row[columnSetting.sectionId];
        const headerId = columnSetting.name;
        const filter = this.filters.find(f => f.type === headerId);

        if (
          filter &&
          !isObservableArraysOrStringsEqual(rowBySection[headerId].value, filter.value) &&
          shouldShow
        ) {
          shouldShow = false;
          return;
        }
      });

      return shouldShow;
    });

    return { ...this.props.data, tbody: orderBy(filteredBody, getters, modes) };
  });

  async componentDidMount() {
    this.recalculateScroll();

    window.addEventListener('resize', this.recalculateScroll);
    if (this.props.account_filters !== null) {
      this.filters = this.props.account_filters;
    }

    this.recalculateFilteredData()
    this.isRestoring.set(false);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.recalculateScroll);
  }

  recalculateFilteredData = () => {
    this.data = selector(() => {
    const getters = [];
    const modes = [];

    const futureDate = new Date(3999, 1, 1); // used to show rows without date at first

    this.sortingMap.forEach((mode, key) => {
      if (mode === 'none') {
        return;
      }

      getters.push(row => {
        const value = getValueFromRowByHeader(row, key);

        if (value && this.props.toNumberKeys.includes(key)) {
          return parseInt(value, 10);
        }

        if (this.datesHeaders.includes(key)) {
          if (value) {
            if (!parsedDatesCache[value]) {
              if (this.props.datetimesKeys.includes(key)) {
                parsedDatesCache[value] = moment(value, 'DD.MM.YYYY HH:mm:ss');
                return parsedDatesCache[value];
              }

              if (typeof value === 'number') {
                // unix timestamp received
                parsedDatesCache[value] = moment(value);
              } else {
                parsedDatesCache[value] = moment(value, 'DD.MM.YYYY');
              }
            }

            return parsedDatesCache[value];
          } else {
            return futureDate;
          }
        }

        return value;
      });

      modes.push(mode);
    });

    const filteredBody = this.props.data.tbody.filter(row => {
      let shouldShow = true;

      this.headersOrder.get().forEach(columnSetting => {
        const rowBySection = row[columnSetting.sectionId];
        const headerId = columnSetting.name;
        const filter = this.filters.find(f => f.type === headerId);

        if (
          filter &&
          !isObservableArraysOrStringsEqual(rowBySection[headerId].value, filter.value) &&
          shouldShow
        ) {
          shouldShow = false;
          return;
        }
      });

      return shouldShow;
    });

    return { ...this.props.data, tbody: orderBy(filteredBody, getters, modes) };
  });
  };

  recalculateScroll = () => {
    this.recalculateTableScrollLeft();
    this.recalculateTableScrollRight();
  };

  recalculateTableScrollRight = () => {
    this.tableContainerRectRight = this.tableContainerEl.getBoundingClientRect().right;
    this.onTableScrollRight();
  };

  recalculateTableScrollLeft = () => {
    this.tableContainerRectLeft = this.tableContainerEl.getBoundingClientRect().left;
    this.onTableScrollLeft();
  };

  scrollTableToRight = () => {
    this.tableContainerEl.scrollLeft = this.tableEl.offsetWidth;
  };

  scrollTableToLeft = () => {
    this.tableContainerEl.scrollLeft = 0;
  };

  onPageChange = async page => {
    await this.props.onPageChange(page);
    this.onTableScroll();
  };

  onTableScroll = () => {
    this.onTableScrollLeft();
    this.onTableScrollRight();
  };

  onTableScrollRight = () => {
    let inViewPorts = 0;
    this.headerEls.forEach(headerEl => {
      if (headerEl && !headerEl.isConnected) {
        return;
      }

      if (headerEl && headerEl.getBoundingClientRect().left >= this.tableContainerRectRight) {
        inViewPorts++;
      }
    });

    this.headersInViewPortRight.set(inViewPorts);
  };

  onTableScrollLeft = () => {
    let inViewPorts = 0;
    this.headerEls.forEach(headerEl => {
      if (headerEl && !headerEl.isConnected) {
        return;
      }

      if (headerEl && headerEl.getBoundingClientRect().right <= this.tableContainerRectLeft) {
        inViewPorts++;
      }
    });

    this.headersInViewPortLeft.set(inViewPorts);
  };

  addFilter = async (type, label, value, isServerSide) => {
    if (['accounts', 'reports', 'transfers', 'staff'].includes(this.props.name)) {
      await this.props.onAddFilter({ type: type, value: value, is_new: true });

      if (this.props.name === 'accounts') {
        await this.props.onSearchClick();
      } else {
        this.filters.push({ type, value, label, isServerSide });
      }

    } else {
      const isFilterAlreadyExist = this.filters.find(f => f.type === type && f.value === value);
      if (!isFilterAlreadyExist) {
        this.filters.push({ type, value, label, isServerSide });
      }
      if (this.props.onAddFilter) {
        await this.props.onAddFilter([
          ...this.filters.filter(filter => filter.isServerSide),
          { type, value, label, isServerSide}
        ]);
      }
    }
  };

  removeFilter = async (type, value) => {
    if (['accounts', 'reports', 'transfers', 'staff'].includes(this.props.name)) {
      await this.props.onRemoveFilter({ type: type, value: value, is_new: false });
      if (this.props.name === 'accounts') {
        await this.props.onSearchClick();
      } else {
        const idx = this.filters.findIndex(f => f.type === type && f.value === value);
        if (idx !== -1) {
          this.filters.splice(idx, 1);
        }
      }
    } else {
      const idx = this.filters.findIndex(f => f.type === type && f.value === value);
      if (idx !== -1) {
        this.filters.splice(idx, 1);
      }
      if (this.props.onRemoveFilter) {
        await this.props.onRemoveFilter(this.filters.filter(filter => filter.isServerSide));
      }
    }
  };

  headersOrder = selector(() => {
    return this.props.selectors.selectedPreset.columnsConfiguration;
  });


  onSort = ({ mode, header }) => {
    if (this.props.isServerSide) {
      this.props.onSortChange && this.props.onSortChange(header, mode);
      return;
    }
    this.sortingMap.set(header, mode);
  };

  render() {
    const thead = (
      <DataTableHeader
        data={this.data}
        sortingMap={this.sortingMap}
        headersOrder={this.headersOrder}
        headerEls={this.headerEls}
        columnSettings={this.props.columnSettings}
        onSort={this.onSort}
        headerSticky={this.props.headerSticky}
      />
    );


    return this.props.loading ? (
      <div className='table-container'>
        <div className='spinner-loading' style={{ paddingTop: '75px', textAlign: 'center' }}>
          <Spinner name='folding-cube' fadeIn='none' />
        </div>
      </div>
    ) : (
      <div className='table-container'>
        <DataTableCaption data={this.data} isOpened={this.isOpened} />
        <div
          ref={node => (this.tableContainerEl = node)}
          className='table-container__body'
          onScroll={this.onTableScroll}
          style={{ display: this.isOpened.get() ? 'block' : 'none' }}
        >
          <table
            ref={node => (this.tableEl = node)}
            className={cx(['table', { tablefixed: this.props.sticky }])}
          >
            {!this.props.headerSticky ? thead : null}
            {this.isRestoring.get() ? null : (
              <DataTableBody
                data={this.data}
                headersOrder={this.headersOrder}
                columnSettings={this.props.columnSettings}
                favorites={this.props.favorites}
                toggleFavorite={this.props.toggleFavorite}
                onFilter={this.addFilter}
                isDecimalThunk={this.props.isDecimalThunk}
                requestModalOpen={this.props.requestModalOpen}
                valueMapper={this.props.valueMapper}
                tooltipContent={this.props.tooltipContent}
              />
            )}
            {this.props.headerSticky ? thead : null}
            <DataTableFooter
              data={this.data}
              headersOrder={this.headersOrder}
              isDecimalThunk={this.props.isDecimalThunk}
              columnSettings={this.props.columnSettings}
            />
          </table>
          {!!this.props.currentPage && (
            <Pagination
              activePage={this.props.currentPage}
              itemsCountPerPage={this.props.pageSize}
              totalItemsCount={this.props.pages * this.props.pageSize}
              pageRangeDisplayed={5}
              onChange={this.onPageChange}
            />
          )}
          <DataTableCounter
            data={this.props.body}
            onScrollTableTo={this.scrollTableToRight}
            headersInViewPort={this.headersInViewPortRight}
            position='right'
          />
          <DataTableCounter
            data={this.props.body}
            onScrollTableTo={this.scrollTableToLeft}
            headersInViewPort={this.headersInViewPortLeft}
            position='left'
          />
        </div>
        <DataTableFilters
          filters={this.props.account_filters !== null ? this.props.account_filters : this.filters}
          onAdd={this.addFilter}
          onRemove={this.removeFilter}
        />
      </div>
    );
  }
}

export default Component({})(DataTable);
